import React, { useState, useEffect } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Spinner from "@amzn/awsui-components-core/spinner";
import { useUser } from 'src/context/UserContext';
import { AssignedTraining, fetchAssignedTrainings } from 'src/services/learn-api';
import Table from "@amzn/awsui-components-core/table";
import TruncatedText from "@amzn/awsui-components-core/truncated-text";
import { MOCK_ASSIGNED_TRAININGS } from 'src/mock/learn-api.mock';
import './AssignedTrainings.scss';

// Toggle this to false to use the Learn API
const USE_MOCK_DATA = false;

const STATUS_LABELS: Record<string, string> = {
    CANCELLED: 'Cancelled',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
    IN_PROGRESS: 'In Progress',
    REGISTERED: 'Registered',
    REQUESTED: 'Requested'
};

const STATUS_CLASS: Record<string, string> = {
    CANCELLED: 'status--cancelled',
    COMPLETED: 'status--completed',
    FAILED: 'status--failed',
    IN_PROGRESS: 'status--in-progress',
    REGISTERED: 'status--registered',
    REQUESTED: 'status--requested'
};

const formatDate = (dateStr: string): string => {
    try {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return dateStr;
    }
};

const AssignedTrainings = () => {
    const { user } = useUser();
    const [trainings, setTrainings] = useState<AssignedTraining[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sortingColumn, setSortingColumn] = useState<{ sortingField: string }>({ sortingField: 'dueDate' });
    const [sortingDescending, setSortingDescending] = useState(false);

    useEffect(() => {
        loadTrainings();
    }, [user]);

    const loadTrainings = async () => {
        setLoading(true);
        setError(null);
        try {
            if (USE_MOCK_DATA) {
                setTrainings(MOCK_ASSIGNED_TRAININGS);
            } else {
                // Real API call — requires backend proxy to be set up
                const alias = user?.alias || '';
                if (!alias) {
                    setTrainings([]);
                    return;
                }
                const data = await fetchAssignedTrainings(alias);
                setTrainings(data || []);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to load assigned trainings');
        } finally {
            setLoading(false);
        }
    };

    // Split into incomplete (top) and completed (bottom)
    const incomplete = trainings.filter(t => t.status !== 'COMPLETED');

    const sortedItems = [...incomplete].sort((a, b) => {
        const field = sortingColumn.sortingField as keyof AssignedTraining;
        const aVal = (a[field] || '') as string;
        const bVal = (b[field] || '') as string;
        const cmp = aVal.localeCompare(bVal);
        return sortingDescending ? -cmp : cmp;
    });

    return (
        <div className="section-card assigned-trainings">
            <div className="section-card__header">
                <h3 className="section-card__title">Assigned Trainings</h3>
            </div>

            <div className="section-card__body">
            {error && (
                <Alert
                    dismissible
                    type="error"
                    onDismiss={() => setError(null)}
                >
                    <strong>Error: </strong>{error}
                </Alert>
            )}

            {loading && <Spinner />}

            {!loading && trainings.length === 0 && !error && (
                <p className="section-card__placeholder">
                    No assigned trainings found.
                </p>
            )}

            {!loading && trainings.length > 0 && (
                <div>
                    {incomplete.length > 0 && (
                        <div>
                            <Table
                                items={sortedItems}
                                columnDefinitions={[
                                    {
                                        id: 'title',
                                        header: 'Title',
                                        cell: (item: AssignedTraining) => (
                                            <TruncatedText>{item.title}</TruncatedText>
                                        ),
                                        sortingField: "title",
                                        width: 140,
                                        maxWidth: 200,
                                        isRowHeader: true
                                    },
                                    {
                                        id: 'status',
                                        header: 'Status',
                                        cell: (item: AssignedTraining) => (
                                            <span className={`assigned-trainings__status ${STATUS_CLASS[item.status] || ''}`}>
                                                {STATUS_LABELS[item.status] || item.status}
                                            </span>
                                        ),
                                        sortingField: "status"
                                    },
                                    {
                                        id: 'dueDate',
                                        header: 'Due Date',
                                        cell: (item: AssignedTraining) => item.dueDate ? formatDate(item.dueDate) : '-',
                                        sortingField: "dueDate"
                                    },
                                    {
                                        id: 'statusDate',
                                        header: 'Status Date',
                                        cell: (item: AssignedTraining) => formatDate(item.statusDatetime),
                                        sortingField: "statusDatetime"
                                    },
                                ]}
                                variant="embedded"
                                sortingColumn={sortingColumn}
                                sortingDescending={sortingDescending}
                                onSortingChange={({ detail }) => {
                                    setSortingColumn(detail.sortingColumn as { sortingField: string });
                                    setSortingDescending(detail.isDescending ?? false);
                                }}
                            />
                        </div>
                    )}
                </div>
            )}
            </div>
        </div>
    );
};

export default AssignedTrainings;
